/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * SSF-413 follow-up — kit-level availability helpers.
 *
 * Kit components render exclusively inside the collapsed `<KitGroupCard>` /
 * `<MiniCartKitGroup>` presentation (see `product-items-list/index.tsx` and
 * `cart-sheet.tsx` — PLIs carrying `c_partOfKitNumber` are explicitly skipped
 * as standalone `<ProductItem>` / `<MiniCartItem>` rows). The row-level
 * `<CartDeliveryEstimate>` "unavailable" copy that regular rows render
 * therefore never fires for a kit, so both kit cards need to detect the
 * signal themselves and feed it into the SAME `<CartDeliveryEstimate>`
 * component (no bespoke banner / icon treatment).
 *
 * A kit is atomic — any unavailable component makes the whole kit unbuyable,
 * matching how the Proceed-to-Checkout gate (`getCheckoutBlockReason` in
 * `unavailable-item.ts`) already reasons about it. We surface the first
 * unavailable component as the delivery-estimate anchor; the caller only
 * needs its `c_atpIsAvailable` field so any `false` component is
 * interchangeable for the purpose of triggering the 'unavailable' variant.
 */

function isAtpUnavailable(item: unknown): boolean {
    if (!item || typeof item !== 'object') return false;
    const record = item as { c_atpIsAvailable?: unknown; c_priceIsAvailable?: unknown };
    if (record.c_atpIsAvailable === false || record.c_atpIsAvailable === 'false') return true;
    // Pricing-availability block — a kit component whose zone-book coverage
    // is missing is also "unbuyable", and a kit is atomic. Reuses the same
    // detector so the kit-card unavailable copy fires for price-unavailable
    // components without a bespoke variant. Legit-$0 exempt PLI types
    // (warranty / *RETURN / bundled / option / bonus) are stamped `true` or
    // left undefined by CalculateBasket, so this branch never falses on
    // them. Name kept as `isAtpUnavailable` for source-compat with the
    // existing callers; a rename would ripple through cart-card, mini-cart-
    // kit-card, and their tests for no functional gain.
    if (record.c_priceIsAvailable === false || record.c_priceIsAvailable === 'false') return true;
    return false;
}

/**
 * Return the first component with a persisted `c_atpIsAvailable === false`
 * verdict, or `null` when none of the components are unavailable. The
 * returned value can be handed straight to `<CartDeliveryEstimate>` — its
 * `c_atp*` fields will drive the 'unavailable' variant just as they do for a
 * regular row.
 */
export function getFirstAtpUnavailableComponent<T>(items: readonly T[] | null | undefined): T | null {
    if (!items?.length) return null;
    for (const item of items) {
        if (isAtpUnavailable(item)) return item;
    }
    return null;
}

/**
 * Convenience predicate — kept for callers / tests that only need the
 * boolean signal (e.g. "does the kit have any unavailable component?").
 */
export function hasKitAtpUnavailableComponent(items: readonly unknown[] | null | undefined): boolean {
    return getFirstAtpUnavailableComponent(items) !== null;
}
